import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Upload, Mail, Phone, ArrowRight, Loader2, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'sonner';
import OrderProgressTracker from '../components/OrderProgressTracker';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orderData, setOrderData] = useState(null);
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!orderId) {
        navigate('/pricing');
        return;
      }

      // Skip backend lookup for synthetic test orders generated from the admin panel
      const isTestOrder = orderId.startsWith('TEST-');

      try {
        if (isTestOrder) {
          setOrderData({
            orderId: orderId,
            packageName: 'Test Order',
            totalPrice: 0,
            depositAmount: 0,
            email: '',
            name: ''
          });
        } else {
          const response = await axios.get(`${API}/api/order/${orderId}`);
          const order = response.data?.order;
          if (order) {
            setOrderData({
              orderId: order.order_id,
              packageName: order.package_type,
              totalPrice: order.total_price,
              depositAmount: order.deposit_amount,
              addons: order.addons || [],
              email: order.email,
              name: order.name,
              uploadLinkEmailSent: order.upload_link_email_sent,
              productionStage: order.production_stage ?? 0,
              deliveryAddress: order.delivery_address || null
            });

            // Fire-and-forget: send the QR/upload link email (idempotent on backend)
            if (!order.upload_link_email_sent) {
              axios.post(`${API}/api/order/${orderId}/send-upload-email`).catch(() => {});
            }
          } else {
            setOrderData({
              orderId: orderId,
              packageName: 'Your Order',
              totalPrice: 0,
              depositAmount: 0,
              email: '',
              name: ''
            });
          }
        }
      } catch (error) {
        console.error('Error fetching order:', error);
        setOrderData({
          orderId: orderId,
          packageName: 'Your Order',
          totalPrice: 0,
          depositAmount: 0,
          email: '',
          name: ''
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId, navigate]);

  const uploadUrl = `${window.location.origin}/upload/${orderId}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(uploadUrl);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-xl">Loading your order details...</p>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-12 px-4">
      <div className="container mx-auto">
        <div className="max-w-3xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500 mb-4">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Payment Successful!
            </h1>
            <p className="text-xl text-gray-300">
              Thank you for choosing Visual Creationz
            </p>
          </div>

          {/* Order Details */}
          {orderData.totalPrice > 0 && (
            <div className="bg-white/5 rounded-2xl p-8 border border-white/10 mb-6" data-testid="order-summary">
              <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
              <div className="space-y-3 text-gray-300">
                <div className="flex justify-between">
                  <span>Order ID:</span>
                  <span className="font-mono text-amber-500">{orderData.orderId}</span>
                </div>
                {orderData.name && (
                  <div className="flex justify-between">
                    <span>Name:</span>
                    <span className="font-semibold">{orderData.name}</span>
                  </div>
                )}
                {orderData.packageName !== 'Your Order' && (
                  <>
                    <div className="flex justify-between">
                      <span>Package:</span>
                      <span className="font-semibold">{orderData.packageName}</span>
                    </div>
                    {(orderData.addons || []).map((a) => (
                      <div key={a.id} className="flex justify-between" data-testid={`success-addon-${a.id}`}>
                        <span>{a.name}{a.quantity > 1 ? ` (${a.quantity})` : ''}:</span>
                        <span className="font-semibold">+£{Number(a.price).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between">
                      <span>Total:</span>
                      <span className="font-semibold">£{Number(orderData.totalPrice).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Deposit Paid:</span>
                      <span className="font-semibold text-green-400">£{Number(orderData.depositAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between" data-testid="success-remaining-balance">
                      <span>Remaining Balance:</span>
                      <span className="font-semibold">£{Math.max(Number(orderData.totalPrice) - Number(orderData.depositAmount), 0).toFixed(2)}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Project Progress Tracker */}
          {orderData.totalPrice > 0 && (
            <div className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10 mb-6">
              <OrderProgressTracker stage={orderData.productionStage || 0} />
            </div>
          )}

          {/* USB Delivery Address confirmation */}
          {orderData.deliveryAddress && (
            <div className="bg-white/5 rounded-2xl p-6 md:p-8 border border-white/10 mb-6" data-testid="payment-success-delivery-address">
              <div className="flex items-center gap-2 mb-3">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-400"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <h2 className="text-lg md:text-xl font-semibold text-white">Your USB will be delivered to</h2>
              </div>
              <div className="text-sm text-gray-200 space-y-0.5 ml-7">
                <div className="text-white font-medium">{orderData.deliveryAddress.name}</div>
                {orderData.deliveryAddress.line1 && <div>{orderData.deliveryAddress.line1}</div>}
                {orderData.deliveryAddress.line2 && <div>{orderData.deliveryAddress.line2}</div>}
                <div>{[orderData.deliveryAddress.city, orderData.deliveryAddress.state, orderData.deliveryAddress.postal_code].filter(Boolean).join(', ')}</div>
                {orderData.deliveryAddress.country && <div className="text-gray-400">{orderData.deliveryAddress.country}</div>}
              </div>
              <p className="text-xs text-gray-500 mt-3 ml-7">
                Need to change this? Contact us straight away.
              </p>
            </div>
          )}

          {/* QR Code Upload Section - MAIN FEATURE */}
          <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-2 border-blue-500/50 rounded-2xl p-8 mb-6">
            <div className="text-center mb-6">
              <Upload className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h2 className="text-3xl font-bold mb-2">📱 Upload Your Media</h2>
              <p className="text-gray-300">
                Scan this QR code with your phone to easily upload photos and videos
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl w-fit mx-auto mb-6 shadow-2xl">
              <QRCodeSVG 
                value={uploadUrl}
                size={220}
                level="H"
                includeMargin={true}
              />
            </div>

            <div className="text-center mb-6">
              <p className="text-sm text-gray-400 mb-3">Or copy this upload link:</p>
              <div className="bg-white/10 border border-white/20 rounded-lg p-4 font-mono text-sm break-all mb-3">
                {uploadUrl}
              </div>
              <button
                onClick={copyToClipboard}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition-all"
              >
                <Copy size={16} />
                Copy Link
              </button>
            </div>

            <button
              onClick={() => navigate(`/upload/${orderData.orderId}`)}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white py-4 rounded-full font-bold hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2 text-lg"
            >
              <Upload size={20} />
              Upload Files Now
              <ArrowRight size={20} />
            </button>
          </div>

          {/* What's Next */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10 mb-6">
            <h2 className="text-2xl font-bold mb-4">🎬 What Happens Next?</h2>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-black flex items-center justify-center font-bold">
                  1
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Upload Your Media</h3>
                  <p className="text-sm text-gray-400">
                    Use the QR code above to upload your photos and videos from any device. You can upload as many times as you need!
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-black flex items-center justify-center font-bold">
                  2
                </div>
                <div>
                  <h3 className="font-semibold mb-1">We Create Your Masterpiece</h3>
                  <p className="text-sm text-gray-400">
                    Our team will professionally edit your cinematic presentation with music, transitions, and effects
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 text-black flex items-center justify-center font-bold">
                  3
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Review & Receive</h3>
                  <p className="text-sm text-gray-400">
                    You'll receive your video for review, with revisions included in your package
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-white/5 rounded-2xl p-8 border border-white/10">
            <h2 className="text-2xl font-bold mb-4">Need Help?</h2>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="text-amber-500" size={20} />
                <a href="mailto:VisualCreationz@hotmail.com" className="hover:text-amber-500 transition-colors">
                  VisualCreationz@hotmail.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="text-amber-500" size={20} />
                <a href="tel:07494164379" className="hover:text-amber-500 transition-colors">
                  07494 164379
                </a>
              </div>
            </div>
            {orderData.email && (
              <p className="text-sm text-gray-400 mt-4">
                A confirmation email has been sent to {orderData.email}
              </p>
            )}
          </div>

          {/* Back Button + Track Project Link */}
          <div className="text-center mt-8 space-y-3">
            {orderData.totalPrice > 0 && (
              <div>
                <button
                  onClick={() => navigate(`/order/${orderData.orderId}`)}
                  data-testid="payment-success-track-btn"
                  className="text-amber-500 hover:text-amber-400 font-semibold transition-colors underline underline-offset-4"
                >
                  Bookmark this link to track your project →
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  {window.location.origin}/order/{orderData.orderId}
                </p>
              </div>
            )}
            <button
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white transition-colors block mx-auto"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
